import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useETSFRecords, type ETSFRecord, type ETSFChecklistItem } from '../../../hooks/useETSFRecords'

const INPUT_CLS = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm'
const LABEL_CLS = 'block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1'

export default function RecordDetail() {
  const { id } = useParams<{ id: string }>()
  const { fetchRecord, fetchChecklistItems, updateChecklistItem, updateRecord } = useETSFRecords()

  const [record, setRecord] = useState<ETSFRecord | null>(null)
  const [checklist, setChecklist] = useState<ETSFChecklistItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)

  const [editForm, setEditForm] = useState<Partial<ETSFRecord>>({})
  const [editChecklist, setEditChecklist] = useState<ETSFChecklistItem[]>([])

  useEffect(() => {
    const loadData = async () => {
      if (!id) return
      setLoading(true)
      setError(null)

      try {
        const rec = await fetchRecord(id)
        setRecord(rec)
        setEditForm(rec)

        const items = await fetchChecklistItems(id)
        setChecklist(items)
        setEditChecklist(items)
      } catch (e: any) {
        setError(e.message)
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [id, fetchRecord, fetchChecklistItems])

  const calculateResult = (): string => {
    if (editChecklist.some(i => i.result === 'fail')) return 'fail'
    if (editChecklist.some(i => i.result === 'advisory')) return 'advisory'
    return 'pass'
  }

  const handleEditChecklistChange = (index: number, key: string, value: any) => {
    const updated = [...editChecklist]
    updated[index] = { ...updated[index], [key]: value }
    setEditChecklist(updated)
  }

  const handleSave = async () => {
    if (!record || !id) return
    setSaving(true)

    try {
      const newResult = calculateResult()

      // Update record
      await updateRecord(id, {
        ...editForm,
        result: newResult,
      })

      // Update checklist items
      for (const item of editChecklist) {
        if (item.id) {
          await updateChecklistItem(item.id, {
            result: item.result,
            notes: item.notes,
          })
        }
      }

      setRecord({ ...record, ...editForm, result: newResult })
      setChecklist(editChecklist)
      setEditing(false)
    } catch (e: any) {
      alert('Error saving: ' + e.message)
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <div className="p-6">Loading...</div>
  if (error) return <div className="p-6 text-red-600">{error}</div>
  if (!record) return <div className="p-6">Record not found</div>

  const resultColor =
    record.result === 'fail'
      ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
      : record.result === 'advisory'
        ? 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200'
        : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <div className="max-w-6xl mx-auto">
        <Link to="/customs/etsf" className="text-blue-600 hover:underline text-sm mb-4 inline-block">
          ← Back
        </Link>

        <div className="flex items-center gap-4 mb-6">
          <div className="flex-1">
            <h1 className="text-2xl font-bold">
              {record.record_type === 'hmrc_inspection'
                ? 'HMRC Inspection'
                : record.record_type === 'facility_check'
                  ? 'Facility Check'
                  : 'Self Audit'}
            </h1>
            <div className="text-sm text-gray-500 mt-1">{new Date(record.record_date).toLocaleDateString('en-GB')}</div>
          </div>
          <span className="px-3 py-1 rounded text-sm font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
            {record.location}
          </span>
          <span className={`px-3 py-1 rounded text-sm font-medium ${resultColor}`}>{record.result?.toUpperCase() || '-'}</span>
          {!editing && (
            <button onClick={() => setEditing(true)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-800">
              Edit
            </button>
          )}
        </div>

        {/* Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="font-semibold mb-4 text-gray-700 dark:text-gray-300">Record Information</h2>
            <div className="space-y-4">
              <div>
                <label className={LABEL_CLS}>Date</label>
                {editing ? (
                  <input type="date" className={INPUT_CLS} value={editForm.record_date || ''} onChange={e => setEditForm(f => ({ ...f, record_date: e.target.value }))} />
                ) : (
                  <div className="text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 rounded px-3 py-2">{new Date(record.record_date).toLocaleDateString('en-GB')}</div>
                )}
              </div>
              <div>
                <label className={LABEL_CLS}>Job Ref</label>
                {editing ? (
                  <input type="text" className={INPUT_CLS} value={editForm.job_ref || ''} onChange={e => setEditForm(f => ({ ...f, job_ref: e.target.value.toUpperCase() }))} />
                ) : (
                  <div className="text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 rounded px-3 py-2 font-mono">{record.job_ref || '-'}</div>
                )}
              </div>
              <div>
                <label className={LABEL_CLS}>Conducted By</label>
                {editing ? (
                  <input type="text" className={INPUT_CLS} value={editForm.conducted_by || ''} onChange={e => setEditForm(f => ({ ...f, conducted_by: e.target.value }))} />
                ) : (
                  <div className="text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 rounded px-3 py-2">{record.conducted_by || '-'}</div>
                )}
              </div>
              <div>
                <label className={LABEL_CLS}>Inspector Name</label>
                {editing ? (
                  <input type="text" className={INPUT_CLS} value={editForm.inspector_name || ''} onChange={e => setEditForm(f => ({ ...f, inspector_name: e.target.value }))} />
                ) : (
                  <div className="text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 rounded px-3 py-2">{record.inspector_name || '-'}</div>
                )}
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="font-semibold mb-4 text-gray-700 dark:text-gray-300">Action Items</h2>
            <div className="space-y-4">
              <div>
                <label className={LABEL_CLS}>Action Required</label>
                {editing ? (
                  <textarea className={INPUT_CLS} rows={2} value={editForm.action_required || ''} onChange={e => setEditForm(f => ({ ...f, action_required: e.target.value }))} />
                ) : (
                  <div className="text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 rounded px-3 py-2 min-h-[72px]">{record.action_required || '-'}</div>
                )}
              </div>
              <div>
                <label className={LABEL_CLS}>Action Completed</label>
                {editing ? (
                  <input
                    type="checkbox"
                    className="w-4 h-4"
                    checked={editForm.action_completed || false}
                    onChange={e => setEditForm(f => ({ ...f, action_completed: e.target.checked }))}
                  />
                ) : (
                  <div className="text-gray-900 dark:text-white">{record.action_completed ? 'Yes' : 'No'}</div>
                )}
              </div>
              <div>
                <label className={LABEL_CLS}>Action Due Date</label>
                {editing ? (
                  <input type="date" className={INPUT_CLS} value={editForm.action_due_date || ''} onChange={e => setEditForm(f => ({ ...f, action_due_date: e.target.value }))} />
                ) : (
                  <div className="text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 rounded px-3 py-2">
                    {record.action_due_date ? new Date(record.action_due_date).toLocaleDateString('en-GB') : '-'}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Notes */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <h2 className="font-semibold mb-4 text-gray-700 dark:text-gray-300">Notes</h2>
          {editing ? (
            <textarea className={INPUT_CLS} rows={3} value={editForm.notes || ''} onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))} />
          ) : (
            <div className="text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 rounded px-3 py-2 min-h-[72px]">{record.notes || '-'}</div>
          )}
        </div>

        {/* Checklist */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <h2 className="font-semibold mb-4 text-gray-700 dark:text-gray-300">Checklist Items</h2>

          {checklist.length > 0 && (
            <div className="space-y-4">
              {checklist.reduce(
                (acc, item, idx) => {
                  const lastCategory = acc[acc.length - 1]?.category
                  const itemToUse = editing ? editChecklist[idx] : item

                  if (itemToUse.category !== lastCategory) {
                    acc.push({ category: itemToUse.category, items: [] })
                  }

                  acc[acc.length - 1].items.push({ item: itemToUse, index: idx })
                  return acc
                },
                [] as { category: string | null; items: { item: ETSFChecklistItem; index: number }[] }[]
              ).map((group, groupIdx) => (
                <div key={groupIdx}>
                  <h3 className="font-medium text-gray-700 dark:text-gray-300 mb-3">{group.category || 'Other'}</h3>
                  <div className="space-y-3 mb-4 pl-4 border-l-2 border-gray-200 dark:border-gray-700">
                    {group.items.map(({ item, index }) => (
                      <div key={item.id} className="flex items-start gap-4">
                        <div className="flex-1">
                          <p className="text-sm text-gray-900 dark:text-white">{item.item_description}</p>
                        </div>
                        {editing ? (
                          <>
                            <select
                              className={INPUT_CLS + ' w-32'}
                              value={editChecklist[index].result || 'na'}
                              onChange={e => handleEditChecklistChange(index, 'result', e.target.value)}
                            >
                              <option value="na">N/A</option>
                              <option value="pass">Pass</option>
                              <option value="fail">Fail</option>
                            </select>
                            <input
                              type="text"
                              className={INPUT_CLS + ' w-40'}
                              value={editChecklist[index].notes || ''}
                              onChange={e => handleEditChecklistChange(index, 'notes', e.target.value)}
                              placeholder="Notes..."
                            />
                          </>
                        ) : (
                          <>
                            <span className="inline-block px-2 py-1 rounded text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                              {item.result ? item.result.toUpperCase() : 'N/A'}
                            </span>
                            <span className="text-sm text-gray-600 dark:text-gray-400 w-40">{item.notes || '-'}</span>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {editing && (
            <div className={`p-3 rounded text-sm font-medium mt-4 ${resultColor}`}>
              Calculated Result: {calculateResult().toUpperCase()}
            </div>
          )}
        </div>

        {/* Edit Actions */}
        {editing && (
          <div className="flex gap-3 justify-end">
            <button onClick={() => { setEditing(false); setEditForm(record); setEditChecklist(checklist) }} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-800">
              Cancel
            </button>
            <button onClick={handleSave} disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
